import React, { useEffect, useState, useCallback, useMemo } from "react"
import { Col, Row, Card } from "react-bootstrap"
import { Button } from '@material-ui/core'
import { ApplicantColumns, ApplicantFilters } from 'constants/application'
import FilterBarModule from "components/shared/FilterBarModule"
import TableComponent from 'components/shared/TableComponent'
import { Tabs } from 'components/shared/Tabs'
import DsIcon from 'components/ds/Icon'
import DsButton from 'components/ds/Button'
import PageHeader from 'components/ds/PageHeader'
import request from 'tools/request'
import FMCSAsearch from "./partial/Modals/FMCSAsearch"
import AuthService from "tools/AuthService"
import strings from "strings"
import "./Applicants.scss"
import DemoModeSwitch from "../DemoMode/DemoModeSwitch"
import { useAppContext } from "../../context/AppContext"
import { getCreditCosts } from "../../actions/applicants"
import { buildApplicantColumns } from "./utils/columnConfig"
import ArchiveDialog from "./partial/Dialog/ArchiveDialog"
import RedirectFCDialog from "./partial/Dialog/RedirectFCDialog"
import OnboardingErrorDialog from "./partial/Dialog/OnboardingErrorDialog"
import { Toast } from "components/shared/Toaster"
import AttachmentExportModal from "./partial/Export/AttachmentExportModal"

const applicantTabs = [
  { label: 'In underwriting', value: 'IN_UNDERWRITING' },
  { label: 'Incomplete applications', value: 'INCOMPLETE' },
  { label: 'Decisioned applications', value: 'DECISIONED' },
  { label: 'All applications', value: null }
];

function Applicants(props) {
  const {
    applicationList,
    getApplicantsList,
    getFmcsaData,
    getFmcsaDataByDOT,
    archiveApplication,
    retryFactorCloudOnboarding,
    fmcsaData,
    fmcsaParam,
    history,
    totalRecords,
    totalPages,
    hideFmcsa,
    showFactorCloudOnboardColumn
  } = props

  const [loading, setLoading] = useState(false)
  const [showModal, setModal] = useState(false)
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({ page: 0, sizePerPage: 20 })
  const [filter, setFilter] = useState({})
    // Sales reps only see the applications shared with them: view only, all of them at once
  const isSalesRep = AuthService.isSalesRep()
  const [activeTab, setActiveTab] = useState("IN_UNDERWRITING")
  const [tabCounts, setTabCounts] = useState({})
  const { isDemoMode } = useAppContext()
  const { motusEnabled } = AuthService.getClientFeatures()
  const creditUsageEnabled = AuthService.getClientFeatures().creditUsageEnabled && !isSalesRep
  const [creditCosts, setCreditCosts] = useState([])

  // Attachments export: customer admins of clients with the flag. The email links here with ?attachmentExport=<id>.
  const canExportAttachments = AuthService.getClientFeatures().applicationExportEnabled && AuthService.hasRole('CUSTOMER_ADMIN')
  const [attachmentExport, setAttachmentExport] = useState(() => {
    const exportId = new URLSearchParams(window.location.search).get('attachmentExport')
    return { open: !!exportId, exportId }
  })
  // A bell notification links here too; if the page is already open it doesn't remount, so follow the URL.
  useEffect(() => history.listen(location => {
    const exportId = new URLSearchParams(location.search).get('attachmentExport')
    if (exportId) setAttachmentExport({ open: true, exportId })
  }), [history])
  const closeAttachmentExport = () => {
    setAttachmentExport({ open: false, exportId: null })
    // Drop only our parameter; keep any other query parameters and the hash.
    const params = new URLSearchParams(window.location.search)
    if (params.has('attachmentExport')) {
      params.delete('attachmentExport')
      const search = params.toString()
      history.replace(`${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`)
    }
  }

  // Dialog states
  const [archiveDialog, setArchiveDialog] = useState({ open: false, applicationUUID: '', applicantName: '' })
  const [redirectFCDialog, setRedirectFCDialog] = useState({ open: false, clientUrl: null, debtorUrl: null })
  const [onboardingErrorDialog, setOnboardingErrorDialog] = useState({
    open: false,
    errorMessage: '',
    applicationUuid: '',
    applicantName: ''
  })

  // Build columns with handlers
  const columns = buildApplicantColumns({
    showFactorCloudOnboardColumn: showFactorCloudOnboardColumn && !isSalesRep,
    onArchiveClick: (row) => setArchiveDialog({
      open: true,
      applicationUUID: row.uuid,
      applicantName: row.applicantName
    }),
    onRedirectFCClick: (row) => setRedirectFCDialog({
      open: true,
      clientUrl: row.factorCloudClientUrl,
      debtorUrl: row.factorCloudDebtorUrl
    }),
    onOnboardingErrorClick: (row) => setOnboardingErrorDialog({
      open: true,
      errorMessage: row.factorCloudErrorMessage || 'An error occurred during FactorCloud onboarding.',
      applicationUuid: row.uuid,
      applicantName: row.applicantName
    })
  })

  useEffect(() => {
    if (creditUsageEnabled) {
      getCreditCosts()()
          .then((res) => setCreditCosts(res || []))
          .catch((err) => {
            console.error('Error fetching credit costs:', err)
            setCreditCosts([])
          })
    }
  }, [creditUsageEnabled])

  const getApplicants = useCallback((pagination = {}, filters = {}) => {
    setLoading(true)
    const filtersWithStatus = activeTab ? { ...filters, group: activeTab } : filters
    getApplicantsList(pagination, filtersWithStatus, isDemoMode).finally(() => setLoading(false))
  }, [getApplicantsList, isDemoMode, activeTab])

  useEffect(() => {
    const filterPayload = { name: search, ...filter }
    getApplicants(pagination, filterPayload)
  }, [getApplicants, search, pagination, filter, activeTab])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setPagination({ page: 0, sizePerPage: 20 })
  }

  useEffect(() => {
    let cancelled = false
    const filterPayload = { name: search, ...filter }

    const countFor = (group) => request(
      'applications',
      'GET',
      null,
      true,
      { 'x-pagination-num': 0, 'x-pagination-limit': 1, 'x-demo-mode': isDemoMode },
      group ? { ...filterPayload, group } : filterPayload
    ).then(res => Number(res?.headers?.['x-pagination-total-records']))

    Promise.allSettled(applicantTabs.map(tab => countFor(tab.value)))
      .then((results) => {
        if (cancelled) return
        const next = {}
        results.forEach((result, index) => {
          const total = result.status === 'fulfilled' ? result.value : NaN
          if (Number.isFinite(total)) next[String(applicantTabs[index].value)] = total
        })
        setTabCounts(next)
      })

    return () => { cancelled = true }
  }, [search, filter, isDemoMode])

  const tabsWithCounts = applicantTabs.map((tab) => {
    const count = tabCounts[String(tab.value)]
    return {
      ...tab,
      // MUI stacks a Tab's children, so the label and its count travel as one
      // element to keep the count on the same line.
      label: count === undefined
        ? tab.label
        : <span className='tabLabel'>{tab.label}<span className='tabCount'>{count}</span></span>
    }
  })

  const handleConfirmArchive = () => {
    archiveApplication(archiveDialog.applicationUUID, true).then(() => {
      const filterPayload = { name: search, ...filter }
      getApplicants(pagination, filterPayload)
    })
    setArchiveDialog({ open: false, applicationUUID: '', applicantName: '' })
  }

  const handleRetryOnboarding = async () => {
    await retryFactorCloudOnboarding(onboardingErrorDialog.applicationUuid)
    Toast({ type: 'success', message: 'Onboarding retried successfully' })
    setOnboardingErrorDialog({ open: false, errorMessage: '', applicationUuid: '', applicantName: '' })
    const filterPayload = { name: search, ...filter }
    getApplicants(pagination, filterPayload)
  }

  // The export takes exactly what the list shows: search, filters and the active tab.
  const exportFilters = useMemo(() => {
    const params = { name: search || undefined, ...filter, group: activeTab || undefined }
    return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
  }, [search, filter, activeTab])

  const handleFilterApply = (filterParams) => {
    setPagination({ page: 0, sizePerPage: 20 })
    setFilter(filterParams)
  }

  const handleCreateApplication = () => {
    if (hideFmcsa || isDemoMode) {
      history.push('/create-applicant')
    } else {
      setModal(true)
    }
  }

  return (
      <div id="applicants">
        <PageHeader
          title={strings.APPLICATIONS}
          lede={strings.APPLICATIONS_LEDE}
          actions={
            <>
              {!isSalesRep && <DemoModeSwitch />}
              {canExportAttachments && (
                  <Button
                      variant='outlined'
                      color='primary'
                      onClick={() => setAttachmentExport({ open: true, exportId: null })}
                  >
                    Export attachments
                  </Button>
              )}
              {!AuthService.isReadOnlyUser() && !isSalesRep && (
                  <Button
                      variant='contained'
                      color='primary'
                      onClick={handleCreateApplication}
                      startIcon={<DsIcon name='plus' size={18} />}
                  >
                    {strings.CREATE_APPLICATION}
                  </Button>
              )}
            </>
          }
        />

        <Tabs value={activeTab} onChange={handleTabChange} data={tabsWithCounts} />

        <Row className="ml-0 mr-0">
          <Col className="pr-lg-0 pl-lg-0 cardBorder">
            <Card className="border-0">
              <FilterBarModule
                  filters={isSalesRep ? ApplicantFilters.filter(item => item.id !== 'salesRepresentative') : ApplicantFilters}
                  textLabel={'Filters'}
                  columns={columns}
                  receiveCustomColumn={() => {}}
                  filterParams={handleFilterApply}
                  placeHolder={strings.APPLICANT.SEARCH_BY_APPLICANT_NAME}
                  onSearch={setSearch}
              />
              <TableComponent
                  className='application-table'
                  isFiltered={Boolean(search || Object.keys(filter || {}).length)}
                  emptyAction={(search || Object.keys(filter || {}).length) ? (
                      <DsButton
                          variant='secondary'
                          size='small'
                          onClick={() => { setSearch(''); setFilter({}); setPagination({ page: 0, sizePerPage: 20 }) }}
                      >
                          {strings.CLEAR_FILTERS}
                      </DsButton>
                  ) : undefined}
                  data={applicationList}
                  columns={columns}
                  sort={true}
                  loading={loading}
                  totalRecords={totalRecords}
                  totalPages={totalPages}
                  currentPage={pagination.page}
                  paginationLimit={pagination.sizePerPage}
                  onChangePagination={(pagination) => setPagination(pagination)}
                  remoteSorting
              />
            </Card>
          </Col>
        </Row>

        <FMCSAsearch
            show={showModal}
            onHide={() => setModal(false)}
            getFmcsaData={getFmcsaData}
            getFmcsaDataByDOT={getFmcsaDataByDOT}
            fmcsaData={fmcsaData}
            queryUrl={fmcsaParam}
            history={history}
            creditCosts={creditCosts}
            motusEnabled={motusEnabled}
        />

        {canExportAttachments && (
            <AttachmentExportModal
                open={attachmentExport.open}
                exportId={attachmentExport.exportId}
                onClose={closeAttachmentExport}
                filters={exportFilters}
                demoMode={!!isDemoMode}
            />
        )}

        <ArchiveDialog
            open={archiveDialog.open}
            applicantName={archiveDialog.applicantName}
            onConfirm={handleConfirmArchive}
            onClose={() => setArchiveDialog({ open: false, applicationUUID: '', applicantName: '' })}
        />

        <RedirectFCDialog
            open={redirectFCDialog.open}
            clientUrl={redirectFCDialog.clientUrl}
            debtorUrl={redirectFCDialog.debtorUrl}
            onClose={() => setRedirectFCDialog({ open: false, clientUrl: null, debtorUrl: null })}
        />

        <OnboardingErrorDialog
            open={onboardingErrorDialog.open}
            applicantName={onboardingErrorDialog.applicantName}
            errorMessage={onboardingErrorDialog.errorMessage}
            onRetry={handleRetryOnboarding}
            onClose={() => setOnboardingErrorDialog({ open: false, errorMessage: '', applicationUuid: '', applicantName: '' })}
        />
      </div>
  )
}

export default Applicants